import { IonButtons, IonIcon, IonPage, IonRouterLink } from "@ionic/react";
import { Trans, useTranslation } from "react-i18next";
import { useForm } from "@tanstack/react-form";
import { usePostHog } from "posthog-js/react";
import { z } from "zod";
import { AuthContent } from "@/components/Auth/Content";
import { AuthHeader } from "@/components/Auth/Header";
import { AuthPageHeaderTitle } from "@/components/Auth/PageHeaderTitle";
import { AuthStrategyAlert } from "@/components/Auth/Strategy/Alert";
import { AuthStrategyOAuth } from "@/components/Auth/Strategy/OAuth";
import { AuthSubmitButton } from "@/components/Auth/SubmitButton";
import { FormInput } from "@/components/Form/Input";
import { Button } from "@/components/Global/Button";
import { TitleIcon } from "@/components/TitleIcon";
import { backendService } from "@/lib/backend";
import { useExtractedMutation } from "@/lib/backend/utils/hooks";
import { envelopeIcon, keyIcon, sparklesIcon } from "@/lib/icons/@heroicons/react/24/outline";
import { Routes } from "@/lib/router";
import { Sentry } from "@/lib/sentry";
import { useSelectorAuthStore } from "@/lib/store/auth";
import { useAuthFlowStore } from "@/lib/store/auth-flow";
import { logger } from "@/logger";

export function AuthSignInPassword() {
	const posthog = usePostHog();
	const state = useAuthFlowStore();
	const authenticate = useSelectorAuthStore.use.authenticate();
	const { t } = useTranslation();

	const mutationSignIn = useExtractedMutation({
		mutationKey: ["auth", "sign-in"],
		mutationFn: backendService.auth.authControllerSignin,
	});

	const form = useForm({
		defaultValues: {
			email: state.email,
			password: state.password,
		},
		validators: {
			onSubmit: z.object({
				email: z.string().email(t("fields.email.errors.invalid")),
				password: z.string(),
			}),
		},
		onSubmit: ({ value, formApi }) =>
			mutationSignIn.mutateAsync(value, {
				onSuccess: (data) => {
					posthog.capture("User Sign In", { method: "password", success: true });
					authenticate(data);
					state.reset();
				},
				onError: (error) => {
					Sentry.captureException(error);
					posthog.capture("User Sign In", { method: "password", success: false });

					logger.getLogger("Auth").error({ error }, "Sign In Failed");

					formApi.setFieldMeta("password", (current) => ({
						...current,
						errorMap: {
							onSubmit: t("fields.password.errors.invalid"),
						},
					}));
				},
			}),
	});

	return (
		<IonPage>
			<AuthHeader>
				<IonButtons slot="primary">
					<Button
						data-attr="page.auth.sign-in.header.primary-button"
						routerLink={Routes.AuthSignUp()}
						routerDirection="forward"
						variant="link"
						className="part-native:text-brown-600 text-sm font-normal"
					>
						<span>
							<Trans i18nKey="page.auth.sign-in.header.primary-button" />
						</span>
					</Button>
				</IonButtons>
			</AuthHeader>
			<AuthContent>
				<TitleIcon icon={sparklesIcon} />

				<AuthPageHeaderTitle
					parts={{
						title: { children: t("page.auth.sign-in.title") },
						subtitle: { children: t("page.auth.sign-in.subtitle") },
					}}
				/>

				<form
					onSubmit={(event) => {
						event.preventDefault();
						event.stopPropagation();
						form.handleSubmit();
					}}
					className="flex flex-col gap-6"
				>
					<form.Field
						name="email"
						children={(field) => (
							<FormInput
								data-attr="page.auth.sign-in.password.email"
								field={field}
								type="email"
								label={t("fields.email.label")}
								autocomplete="email"
								inputMode="email"
								placeholder={t("fields.email.placeholder")}
							>
								<IonIcon slot="start" icon={envelopeIcon} aria-hidden="true" />
							</FormInput>
						)}
					/>

					<form.Field
						name="password"
						children={(field) => (
							<FormInput
								data-attr="page.auth.sign-in.password.password"
								field={field}
								type="password"
								label={t("fields.password.label")}
								autocomplete="current-password"
								placeholder={t("fields.password.placeholder")}
							>
								<IonIcon slot="start" icon={keyIcon} aria-hidden="true" />
							</FormInput>
						)}
					/>

					<div className="flex flex-col gap-4">
						<form.Subscribe
							selector={(state) => [state.canSubmit, state.isSubmitting]}
							children={([canSubmit, isSubmitting]) => (
								<AuthSubmitButton
									data-attr="page.auth.sign-in.password.action.primary"
									disabled={!canSubmit}
									isSubmitting={isSubmitting}
								>
									{t("page.auth.sign-in.steps.password.action.primary")}
								</AuthSubmitButton>
							)}
						/>

						<Button
							data-attr="page.auth.sign-in.password.action.secondary"
							variant="link"
							className="text-brown-600 text-sm font-semibold"
							routerLink={Routes.AuthForgotPassword()}
							routerDirection="forward"
						>
							{t("page.auth.sign-in.steps.password.action.secondary")}
						</Button>

						<AuthStrategyAlert>
							<Trans
								i18nKey="page.auth.sign-in.steps.password.alert"
								components={[
									<IonRouterLink
										key="0"
										data-attr="page.auth.sign-in.magic.strategy"
										routerLink={Routes.AuthSignInMagic()}
										routerDirection="back"
										className="inline font-bold hover:underline focus:underline active:underline"
									/>,
								]}
							/>
						</AuthStrategyAlert>
					</div>
				</form>

				<AuthStrategyOAuth />
			</AuthContent>
		</IonPage>
	);
}
